import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import EURegistrationAlert from './EURegistrationAlert';
import TermsAndConditions from './TermsAndConditions';
import './Form.css';

const SignupForm = () => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(true);
  const [showNonEUAlert, setShowNonEUAlert] = useState(false);
  const [locationInfo, setLocationInfo] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Captcha state
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Fetch captcha challenge when component mounts and whenever needed
  const fetchCaptcha = () => {
    fetch('/api/captcha')
      .then((response) => response.json())
      .then((data) => {
        setCaptchaQuestion(data.question);
        setCaptchaToken(data.token);
      })
      .catch((err) => console.error('Error fetching captcha:', err));
  };

  useEffect(() => {
    fetchCaptcha();
  }, []);

  const checkUsernameAvailability = async (username) => {
    try {
      const response = await fetch(`/api/check-username?username=${username}`);
      const data = await response.json();
      setIsUsernameAvailable(data.available);
    } catch (error) {
      console.error('Error checking username availability:', error);
      setIsUsernameAvailable(false);
    }
  };

  const handleUsernameChange = (e) => {
    const newUsername = e.target.value;
    setUsername(newUsername);
    if (newUsername) {
      checkUsernameAvailability(newUsername);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    // Basic field validation
    if (!username || !email || !password || !country || !city || !postalCode) {
      setError('Alle Felder sind erforderlich.');
      return;
    }

    if (!emailRegex.test(email)) {
      setError('Bitte geben Sie eine valide E-Mail-Adresse ein.');
      return;
    }

    // Validate country code: must be exactly 2 characters
    if (country.trim().length !== 2) {
      setError('Bitte geben Sie einen gültigen 2-stelligen Ländercode ein (z.B. DE, AT, CH).');
      return;
    }

    // Ensure captcha answer has been provided.
    if (!captchaAnswer) {
      setError('Bitte geben Sie die Antwort zum Captcha ein.');
      return;
    }

    // Check if username is available
    if (!isUsernameAvailable) {
      setError('Benutzername ist bereits vergeben.');
      return;
    }

    // Check if terms are accepted
    if (!termsAccepted) {
      setError('Sie müssen die Nutzungsbedingungen akzeptieren, um fortzufahren.');
      setShowTerms(true);
      return;
    }

    // Show modal with registration hint
    setShowModal(true);
  };

  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    setShowTerms(false);
  };

  const handleDeclineTerms = () => {
    setTermsAccepted(false);
    setShowTerms(false);
  };

  const onModalConfirm = async () => {
    setShowModal(false);
    // Build registration payload including captcha
    const payload = {
      username,
      email,
      password,
      country_code: country.trim(), // sending as country_code to match expected field
      city,
      zip: postalCode,
      phone_number: phoneNumber,
      captchaAnswer,
      captchaToken,
      confirmed: true, // Ensure this field is included
      termsAccepted // Send the terms acceptance
    };

    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      
      if (response.status === 403 && data.details) {
        // Nicht-EU-Land erkannt
        setLocationInfo(data.details);
        setShowNonEUAlert(true);
        return;
      }
      
      if (!response.ok) {
        setError(data.error || 'Registrierung fehlgeschlagen');
      } else {
        setMessage(data.message || 'Registrierung erfolgreich abgeschlossen!');
      }
    } catch (err) {
      setError('Fehler bei der Registrierung');
    } finally {
      // Refresh captcha challenge after submission and reset captcha answer
      fetchCaptcha();
      setCaptchaAnswer('');
    }
  };

  const onModalCancel = () => {
    setShowModal(false);
    setError('Registrierung abgebrochen. Bitte bestätigen Sie den Registrierungshinweis.');
  };

  const closeNonEUAlert = () => {
    setShowNonEUAlert(false);
    setLocationInfo(null);
  };

  return (
    <div className="form-container">
      <h2>Registrierung zur HiFi.Couch & Gebrauchtmarkt</h2>
      
      <div className="private-use-banner">
        <span className="material-symbols-outlined">info</span>
        <p>Diese Plattform darf <strong>ausschließlich für private Zwecke</strong> genutzt werden. Gewerbliche Nutzung ist untersagt.</p>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {message && <div className="success-message">{message}</div>}
      
      <p><b>HINWEIS: es wird für den Geo-Lookup, um Deinen PIN auf der Landkarte darzustellen, nur das Land + Stadt + PLZ verwendet, daher nur sehr grob. Niemand muss sich sorge machen, dass die exakte Adresse angezeigt wird, genau darum fragen wir KEINE Straße und Hausnummer ab!</b></p>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="username">Benutzername:</label>
          <input
            id="username"
            type="text"
            value={username}
            onChange={handleUsernameChange}
            required
          />
          {!isUsernameAvailable && <div className="error-message">Benutzername ist bereits vergeben.</div>}
        </div>
        <div className="form-group">
          <label htmlFor="email">E-Mail:</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Passwort:</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="phoneNumber">Telefonnummer (optional):</label>
          <input
            id="phoneNumber"
            type="tel"
            value={phoneNumber}
            onChange={e => setPhoneNumber(e.target.value)}
            placeholder="+49 123 45678900"
          />
          <small className="form-hint">
            Die Telefonnummer ist optional. Aber bei Nutzung des privaten Gebrauchtmarkts empfehlenswert. Sie können bei jedem Angebot selbst entscheiden, 
            ob diese angezeigt werden soll.
          </small>
        </div>
        <div className="form-group">
          <label htmlFor="country">Ländercode (z.B. DE, AT, CH):</label>
          <input
            id="country"
            type="text"
            value={country}
            onChange={e => setCountry(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="city">Stadt:</label>
          <input
            id="city"
            type="text"
            value={city}
            onChange={e => setCity(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="postalCode">Postleitzahl:</label>
          <input
            id="postalCode"
            type="text"
            value={postalCode}
            onChange={e => setPostalCode(e.target.value)}
            required
          />
        </div>
        
        {/* AGB-Bestätigung */}
        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={() => setShowTerms(true)}
            />
            Ich akzeptiere die <button 
              type="button" 
              className="terms-link"
              onClick={() => setShowTerms(true)}
            >Nutzungsbedingungen &amp; AGB</button>
          </label>
        </div>
        
        {/* Captcha Section */}
        {captchaQuestion && (
          <div className="form-group">
            <label htmlFor="captchaAnswer">{captchaQuestion}</label>
            <input
              id="captchaAnswer"
              type="text"
              value={captchaAnswer}
              onChange={e => setCaptchaAnswer(e.target.value)}
              required
              placeholder="Antwort hier eingeben"
              style={{ backgroundColor: '#66ccff', borderColor: '#ff0000' }}
            />
          </div>
        )}
        <button type="submit">Registrieren</button>
      </form>

      {showModal && (
        <Modal
          title="Registrierungshinweis"
          message="Ihre eingegebenen Daten werden sicher gespeichert und zu keinerlei Marketingzwecken verwendet - AUDIOPHILE COMMUNITY PROJECT! Möchten Sie fortfahren?"
          onConfirm={onModalConfirm}
          onCancel={onModalCancel}
        />
      )}
      
      {showNonEUAlert && (
        <EURegistrationAlert 
          locationInfo={locationInfo}
          onClose={closeNonEUAlert}
        />
      )}
      
      {showTerms && (
        <TermsAndConditions 
          onAccept={handleAcceptTerms}
          onDecline={handleDeclineTerms}
        />
      )}
    </div>
  );
};

export default SignupForm;